import { StyleSheet } from 'aphrodite';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import {
	Dropdown,
	DropdownCheckboxItem,
	DropdownContent,
	DropdownSelectTrigger,
} from '../../../../../web/components-v2/Dropdown';
import { ISelectOption } from '../../../../../web/components/DeprecatedSelect';
import { bs } from '../../../../../web/styles/styles';
import { FieldKey } from '../../../../viewModels/form';
import { IPhoneCallOutcome } from '../../../../viewModels/phonecall';
import { checkAll, filterByChecked, prepareResponse, reduceChildren } from '../OutcomeSelect/utils';

export function OutcomeMultiSelectDropdown({
	onChange,
	selectedOutcomes = [],
	defaultChecked = true,
	defaultValues,
}: {
	onChange?: (item: ISelectOption<FieldKey>[]) => void;
	selectedOutcomes?: ISelectOption<FieldKey>[]; // For pill mode state management
	defaultChecked?: boolean;
	defaultValues?: string[];
}) {
	const { logApiError } = useEventLogging('OutcomeSelectV2');
	const userSession = useUserSession();
	const [outcomeOptions, setOutcomeOptions] = useState<ISelectOption<FieldKey>[]>([]);
	const isInternalUpdateRef = useRef(false);

	const pillOptionsChildrenOnly = outcomeOptions?.reduce(reduceChildren, []);
	const pillCheckedOnly = pillOptionsChildrenOnly?.filter(filterByChecked);
	const pillIsAllSelected = pillCheckedOnly?.length === pillOptionsChildrenOnly?.length;
	const dropdownMessage = pillIsAllSelected
		? 'All outcome types'
		: pillCheckedOnly.length > 0
			? `${pillCheckedOnly.length} outcomes selected`
			: 'Select outcomes...';

	// Update internal state when selectedOutcomes prop changes
	useEffect(() => {
		// Skip update if this was triggered by our own internal change
		if (isInternalUpdateRef.current) {
			isInternalUpdateRef.current = false;
			return;
		}

		if (selectedOutcomes && selectedOutcomes.length > 0) {
			// Update outcomeOptions based on selectedOutcomes
			setOutcomeOptions(prev => {
				const updated = prev.map(option => ({
					...option,
					checked: selectedOutcomes.findIndex(selected => selected.id === option.id) !== -1,
					children: option.children?.map(child => ({
						...child,
						checked: selectedOutcomes.findIndex(selected => selected.id === option.id) !== -1,
					})),
				}));
				return updated;
			});
		}
	}, [selectedOutcomes]);
	useEffect(() => {
		const loadOutcomes = async () => {
			try {
				const value = await userSession.webServiceHelper.callAsync<IPhoneCallOutcome[]>('aida/outcomes', 'POST', []);

				const options = value.map(prepareResponse);
				options.unshift({
					checked: true,
					dataContext: FieldKey.Unknown,
					id: 'all',
					name: 'all',
					text: 'All outcome types',
				});

				options?.map(checkAll(defaultChecked, defaultValues));

				setOutcomeOptions(options);
			} catch (error: any) {
				logApiError('LoadOutcomes-Error', error);

				toast.error(error.systemMessage ?? 'Unable to load outcomes');
			}
		};

		loadOutcomes();
	}, [defaultChecked, defaultValues, logApiError, userSession]);

	const handleOutcomeToggle = (target: ISelectOption<FieldKey>) => {
		const isAll = target.id === 'all';
		const checkSpecific = (parent: ISelectOption<FieldKey>) => {
			if (parent?.id === target.id) {
				parent.checked = target.checked;
			}
			parent?.children?.map((child: ISelectOption<FieldKey>) => {
				if (!Object.prototype.hasOwnProperty.call(Object(child), 'checked')) {
					child.checked = true;
				}
				if (parent?.id === target.id && target.checked) {
					child.checked = true;
				}
				if (parent?.id === target.id && !target.checked) {
					child.checked = false;
				}
				if (child?.id === target.id) {
					child.checked = target.checked;
					if (!target.checked) {
						parent.checked = false;
					}
				}
				return child;
			});
			const childrenCheckedSize = parent?.children?.filter(filterByChecked)?.length;
			if (childrenCheckedSize === parent?.children?.length) {
				parent.checked = true;
			}
			return parent;
		};

		const toggleAllOption = (check: boolean) => (parent: ISelectOption<FieldKey>) => {
			if (parent.id === 'all') {
				parent.checked = check;
			}
			return parent;
		};

		const outcomes = outcomeOptions.map(isAll ? checkAll(target.checked) : checkSpecific);

		const parentCheckedSize = outcomes?.filter(filterByChecked)?.length;

		if (parentCheckedSize === outcomes?.length) {
			outcomes.map(toggleAllOption(true));
		} else {
			outcomes.map(toggleAllOption(false));
		}

		isInternalUpdateRef.current = true;
		setOutcomeOptions(outcomes);
		onChange(outcomes);
	};

	return (
		<Dropdown>
			<DropdownSelectTrigger styles={[bs.wFull]}>{dropdownMessage}</DropdownSelectTrigger>
			<DropdownContent>
				{outcomeOptions.map(parent => {
					return (
						<>
							<DropdownCheckboxItem
								checked={parent.checked}
								onCheckedChange={() => handleOutcomeToggle({ ...parent, checked: !parent.checked })}
							>
								{parent.text}
							</DropdownCheckboxItem>
							{parent.children?.map(child => {
								const childChecked = parent.checked || child.checked;
								return (
									<DropdownCheckboxItem
										key={child.id}
										checked={childChecked}
										onCheckedChange={() => handleOutcomeToggle({ ...child, checked: !child.checked })}
										styles={[s.childCheckboxItem]}
									>
										{child.label || child.text}
									</DropdownCheckboxItem>
								);
							})}
						</>
					);
				})}
			</DropdownContent>
		</Dropdown>
	);
}

const s = StyleSheet.create({
	childCheckboxItem: {
		marginLeft: 20,
		fontSize: 14,
	},
});
